<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import TextTabItem from '@/components/common/molecules/text-tab-item.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { ActivityAction } from '@/types';
import { STATES, useTeamFeaturedArticles } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';
import Divider from '../common/divider.vue';

const layoutVariant = 'next';

const emitter = useEmitter();

const isExpanded = ref(false);
const initialVisibleCount = 3;
const maxVisible = 10;

const selectedActivityAction = ref<ActivityAction>('mark');

const {
  data: featuredArticles,
  state,
  mutate: updateFeaturedArticles,
} = useTeamFeaturedArticles(selectedActivityAction, ref(1), maxVisible);

const isLoading = computed(() => state.value === STATES.PENDING);
const adpDocuments = computed(
  () => featuredArticles.value?.featured_articles ?? [],
);

const visibleDocuments = computed(() =>
  isExpanded.value
    ? adpDocuments.value.slice(0, maxVisible)
    : adpDocuments.value.slice(0, initialVisibleCount),
);
const remainingLabel = computed(
  () =>
    (adpDocuments.value.length > 4
      ? `4〜${Math.min(maxVisible, adpDocuments.value.length)}`
      : '4') + '位を見る',
);

const handleArticleUpdate = () => updateFeaturedArticles();
onMounted(() => emitter.on('article-updated', handleArticleUpdate));
onUnmounted(() => emitter.off('article-updated', handleArticleUpdate));

const { setRef, requestId } = useHomeSectionViewTracking(
  'team_feature_articles_view',
  () => !isLoading.value,
  () => ({
    document_count: adpDocuments.value.length,
    layout_variant: layoutVariant,
  }),
);

type Tab = { id: ActivityAction; text: string };
const tabs = computed<Tab[]>(() => [
  {
    id: 'mark',
    text: 'マーク',
  },
  {
    id: 'view',
    text: '閲覧',
  },
]);

const onSelectTab = (tab: Tab) => {
  selectedActivityAction.value = tab.id;
};

const subtitle = computed(() =>
  selectedActivityAction.value === 'view'
    ? '組織内でよく読まれている記事を、閲覧数の多い順に表示しています。'
    : '組織内でマークを集めている記事を、新しさも加味して注目度の高い順に表示しています。',
);
</script>

<template>
  <div class="featured-articles aconnect-home-component" :ref="setRef">
    <div class="title">
      <DgrIcon
        name="crown"
        :keep-fill="false"
        class="aconnect-home-icon animated icon"
      />
      <router-link
        :to="{ name: 'teamFeaturedArticles' }"
        class="c-title-bold--xm"
        >注目情報ランキング</router-link
      >
      <span class="c-tag c-outlineTag c-outlineTag--small featured-period">
        <DgrIcon name="clock" size="xs" :keep-fill="false" />
        過去7日間
      </span>
    </div>
    <div class="c-text-regular--s subtitle">
      {{ subtitle }}
    </div>
    <div class="tabs">
      <TextTabItem
        v-for="tab in tabs"
        :key="tab.id"
        :text="tab.text"
        :is-selected="selectedActivityAction === tab.id"
        @click-tab="onSelectTab(tab)"
      >
        <div class="tab-text">
          {{ tab.text }}
        </div>
      </TextTabItem>
    </div>
    <Divider class="full-bleed" margin="0 -16px 16px -16px" />
    <div class="loading" v-if="isLoading">
      <DgrLoading />
    </div>
    <template v-else-if="adpDocuments.length > 0">
      <template
        v-for="(document, index) in visibleDocuments"
        :key="`${selectedActivityAction}-${document.id}-rank-${index + 1}`"
      >
        <div class="document-container">
          <div class="rank" :class="`rank-${index + 1}`">
            <DgrIcon
              v-if="index < 3"
              name="crown-fill"
              size="xmedium"
              :keep-fill="false"
              class="rank-icon"
            />
            <span class="rank-label c-title-bold--s">
              {{ index + 1 }}
            </span>
          </div>
          <AdpDocumentCardSmall
            :adp-document="document"
            :rank="index + 1"
            page-name="home"
            feature="team_feature_articles"
            show-published-date
            show-option-menu
            :contents-context="{
              event_name: 'team_feature_articles',
              parent_request_id: requestId,
            }"
          >
            <template v-if="document.view_count" #dateExtra>
              <span class="c-text-regular--s view-count">
                閲覧 {{ document.view_count }}回
              </span>
            </template>
          </AdpDocumentCardSmall>
        </div>
        <Divider
          v-if="index < visibleDocuments.length - 1"
          class="full-bleed"
          margin="16px -16px"
        />
      </template>
      <template v-if="adpDocuments.length > 3">
        <Divider class="full-bleed" margin="16px -16px 0 -16px" />
        <button
          class="c-title-bold--s view-more full-bleed"
          @click="isExpanded = !isExpanded"
        >
          <DgrIcon
            :name="isExpanded ? 'chevron-up' : 'chevron-down'"
            size="small"
            :keep-fill="false"
          />
          {{ isExpanded ? '閉じる' : remainingLabel }}
        </button>
      </template>
      <div v-else class="spacing-16"></div>
    </template>
    <div class="empty" v-else>
      <img class="empty-img" src="@/assets/state-empty-article.png" />
      <div class="c-title-medium--xm">注目情報はまだありません</div>
      <div class="c-text-regular--s">
        組織のメンバーが記事をマークやコメントをすると、関心の高い情報がランキングに表示されます。
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.featured-articles {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 0;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .full-bleed {
    width: auto;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 4px;

    a:hover {
      color: $color-green600;
      text-decoration: underline;
    }

    .icon {
      color: $color-green600;
    }

    .featured-period {
      display: flex;
      gap: 4px;
      height: auto;
      margin-left: auto;
      padding: 4px 8px;
      color: $color-gray800;
    }
  }

  .subtitle {
    margin-top: 12px;
    color: $color-gray800;
  }

  .tabs {
    display: flex;
    padding: 0 16px;
    margin-top: 16px;

    .m-text-tab-item {
      :deep(.m-text-tab-item-body) {
        width: 80px;
        padding: 6px 0;
      }
    }

    .tab-text {
      display: flex;
      align-items: center;
      gap: 2px;
    }
  }

  .document-container {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    column-gap: 4px;
    align-items: start;

    .rank {
      position: relative;
      color: $color-gray600;

      &.rank-1,
      &.rank-2,
      &.rank-3 {
        color: #ffffff;
      }
      &.rank-1 .rank-icon {
        color: $color-yl7;
      }
      &.rank-2 .rank-icon {
        color: $color-gray600;
      }
      &.rank-3 .rank-icon {
        color: $color-yl12;
      }

      .rank-label {
        position: absolute;
        top: 8px;
        left: 12px;
      }
    }

    .view-count {
      margin-left: 16px;
    }
  }

  .document-container:has(.document-info-image a:hover) + .divider {
    border-top-color: $color-gray600;
  }
  .divider:has(+ .document-container .document-info-image a:hover) {
    border-top-color: $color-gray600;
  }

  .view-more {
    display: flex;
    gap: 4px;
    height: 48px;
    margin: 0 -16px;
    background: #ffffff;
    border: none;
    color: $color-green600;

    &:hover {
      text-decoration: underline;
    }
  }

  .loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
  }

  .empty {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;

    .empty-img {
      width: 132px;
      height: 80px;
      object-fit: cover;
    }
    .c-title-medium--xm {
      margin-bottom: 24px;
    }
    .c-text-regular--s {
      text-align: center;
    }
  }
}
</style>
